import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { cn } from '../lib/cn';

export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * Renders the bubble at `document.body` instead of where the `<Tooltip>` sits in the tree.
 *
 * Needed whenever the trigger lives inside a pane that is not the topmost stacking context.
 * Un-portalled, `TooltipContent` is `position: fixed; z-index: 50` — but z-index only orders
 * siblings WITHIN a stacking context, so a bubble raised to 50 inside a low-painting pane still
 * loses to `position: static; z-index: auto` elements in a pane that paints later. Measured in the
 * support module's contact panel on 2026-09-10: the tooltip was open, 260x203, at the right
 * coordinates, opacity 1 — and `elementFromPoint` at its centre returned an `<svg>` from the
 * conversation column. Open, correct, and invisible.
 *
 * Not made the default here: every existing caller renders un-portalled today, and changing where
 * they all mount is not this change's business.
 */
export const TooltipPortal = TooltipPrimitive.Portal;

export const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'z-50 overflow-hidden rounded-md bg-text-primary px-2.5 py-1.5 text-caption text-bg-app',
      'shadow-md',
      'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
      'data-[state=delayed-open]:fade-in-0 data-[state=closed]:fade-out-0',
      'data-[state=delayed-open]:zoom-in-95 data-[state=closed]:zoom-out-95',
      className,
    )}
    {...props}
  />
));
TooltipContent.displayName = 'TooltipContent';
